export type PopupVendor = 'typeform' | 'unbounce' | 'klaviyo' | 'userback';

export interface PopupSignal {
  vendor: PopupVendor;
  action: string;
  id?: string;
}

const VENDORS: Array<{ vendor: PopupVendor; hosts: string[] }> = [
  { vendor: 'typeform', hosts: ['typeform.com'] },
  { vendor: 'unbounce', hosts: ['unbounce.com', 'unbouncepages.com'] },
  { vendor: 'klaviyo', hosts: ['klaviyo.com'] },
  { vendor: 'userback', hosts: ['ubembed.com', 'userback.io'] },
];

const TYPEFORM_ACTIONS: Record<string, string> = {
  'form-ready': 'opened',
  'form-started': 'started',
  'form-submit': 'submitted',
  'form-closed': 'closed',
};

const KLAVIYO_ACTIONS: Record<string, string> = {
  open: 'opened',
  embedOpen: 'opened',
  close: 'closed',
  submit: 'submitted',
  stepSubmit: 'step_submitted',
  redirectToUrl: 'redirected',
  qualify: 'qualified',
};

export function popupVendor(rawUrl: string): PopupVendor | null {
  let hostname: string;
  try {
    hostname = new URL(
      rawUrl,
      'https://www.audicus.com',
    ).hostname.toLowerCase();
  } catch {
    return null;
  }

  const match = VENDORS.find((entry) =>
    entry.hosts.some(
      (host) => hostname === host || hostname.endsWith(`.${host}`),
    ),
  );
  return match?.vendor ?? null;
}

export function popupIdFromUrl(rawUrl: string): string | undefined {
  try {
    const url = new URL(rawUrl, 'https://www.audicus.com');
    return `${url.hostname}${url.pathname}`.replace(/\/$/, '').slice(0, 120);
  } catch {
    return undefined;
  }
}

export function typeformAction(messageType: string): string | null {
  return TYPEFORM_ACTIONS[messageType] ?? null;
}

export function klaviyoAction(messageType: string): string | null {
  return KLAVIYO_ACTIONS[messageType] ?? null;
}

type Track = (event: string, properties?: Record<string, unknown>) => void;

/**
 * Klaviyo, Typeform, Unbounce, and Userback draw their popups in a closed
 * shadow tree or a third-party iframe, so the replay cannot see inside them.
 * These signals put the open, close, and submit on the same session timeline.
 * Field values are never forwarded.
 */
export function watchPopupJourney(track: Track): () => void {
  const recent = new Map<string, number>();
  const iframeState = new Map<string, 'opened' | 'closed'>();

  const record = (signal: PopupSignal) => {
    const key = `${signal.vendor}:${signal.action}:${signal.id ?? ''}`;
    const now = Date.now();
    if (now - (recent.get(key) ?? 0) < 1500) {
      return;
    }
    recent.set(key, now);
    track(`popup_${signal.vendor}_${signal.action}`, {
      vendor: signal.vendor,
      action: signal.action,
      ...(signal.id ? { popup_id: signal.id } : {}),
    });
  };

  const onKlaviyo = (event: Event) => {
    const detail = (event as CustomEvent<{ type?: string; formId?: string }>)
      .detail;
    if (!detail?.type) {
      return;
    }
    const action = klaviyoAction(detail.type);
    if (!action) {
      return;
    }
    record({
      vendor: 'klaviyo',
      action,
      id: detail.formId,
    });
  };

  const onMessage = (event: MessageEvent) => {
    if (popupVendor(event.origin) !== 'typeform') {
      return;
    }
    const data = event.data as { type?: unknown; formId?: unknown } | string;
    const messageType = typeof data === 'string' ? data : data?.type;
    if (typeof messageType !== 'string') {
      return;
    }
    const action = typeformAction(messageType);
    if (!action) {
      return;
    }
    const formId =
      typeof data === 'object' && typeof data.formId === 'string'
        ? data.formId
        : undefined;
    record({ vendor: 'typeform', action, id: formId });
  };

  const onClick = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const link = target.closest('a');
    const href = link?.href;
    if (!href) {
      return;
    }
    const vendor = popupVendor(href);
    if (!vendor) {
      return;
    }
    record({
      vendor,
      action: 'link_clicked',
      id: popupIdFromUrl(href),
    });
  };

  const syncIframe = (iframe: HTMLIFrameElement) => {
    const vendor = popupVendor(iframe.src);
    if (!vendor) {
      return;
    }
    const visible = iframe.offsetWidth > 20 && iframe.offsetHeight > 20;
    const key = `${vendor}:${popupIdFromUrl(iframe.src) ?? iframe.src}`;
    const next = visible ? 'opened' : 'closed';
    const prev = iframeState.get(key);
    if (prev === next) {
      return;
    }
    iframeState.set(key, next);
    if (!prev && next === 'closed') {
      return;
    }
    record({
      vendor,
      action: next,
      id: popupIdFromUrl(iframe.src),
    });
  };

  const syncAll = () => {
    document
      .querySelectorAll<HTMLIFrameElement>('iframe')
      .forEach((iframe) => syncIframe(iframe));
  };

  const resize =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => syncAll());

  const observeIframes = () => {
    document
      .querySelectorAll<HTMLIFrameElement>('iframe')
      .forEach((iframe) => resize?.observe(iframe));
  };

  const mutationObserver = new MutationObserver(() => {
    observeIframes();
    syncAll();
  });
  mutationObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['src', 'style', 'class', 'width', 'height'],
    childList: true,
    subtree: true,
  });
  observeIframes();

  window.addEventListener('klaviyoForms', onKlaviyo);
  window.addEventListener('message', onMessage);
  document.addEventListener('click', onClick, true);
  syncAll();

  return () => {
    mutationObserver.disconnect();
    resize?.disconnect();
    window.removeEventListener('klaviyoForms', onKlaviyo);
    window.removeEventListener('message', onMessage);
    document.removeEventListener('click', onClick, true);
  };
}
